import React, { useState } from 'react';
import { ApiClient } from '@chefaiq/shared/lib/api-client';
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

const EmailTemplates: React.FC = () => {
  
  
  const [registrationOTP, setRegistrationOTP] = useState({
    subject: 'Verify Your Email - ChefAiQ',
    body: `Hi {{firstName}},

You have requested to create an account with ChefAiQ.

Your One-Time Password (OTP) is:

{{OTP}}

This OTP will expire in 15 minutes.

If you did not request this, please ignore this email.

This is an automated email from ChefAiQ. Please do not reply to this email.`
  });

  const [passwordReset, setPasswordReset] = useState({
    subject: 'Password Reset Request',
    body: `Hi {{firstName}},

You have requested to reset your password for your ChefAiQ account.

Your One-Time Password (OTP) is:

{{OTP}}

This OTP will expire in 15 minutes.

If you did not request this password reset, please ignore this email.

This is an automated email from ChefAiQ. Please do not reply to this email.`
  });

  const [welcome, setWelcome] = useState({
    subject: 'Welcome to ChefAiQ!',
    body: `Hi {{firstName}},

Welcome to ChefAiQ! Your account has been successfully created.

You can now start managing your recipes, ingredients, and kitchen operations all in one place.

Get started by:
- Creating your first recipe
- Adding ingredients to your pantry
- Exploring our features

If you have any questions, feel free to reach out to our support team.

Happy cooking!

The ChefAiQ Team

This is an automated email from ChefAiQ. Please do not reply to this email.`
  });

  const [demoRequest, setDemoRequest] = useState({
    customerSubject: 'Thank You for Your Demo Request',
    customerBody: `Hi {{name}},

Thank you for your interest in ChefAiQ!

We have received your demo request and will contact you as soon as possible.

Our team will reach out to you at {{email}} to schedule a personalized demo.

Best regards,
The ChefAiQ Team`,
    adminSubject: 'New Demo Request',
    adminBody: `New demo request received:

Customer Name: {{name}}
Customer Email: {{email}}
Date & Time: {{dateTime}}

Please follow up with the customer as soon as possible.`
  });

  const handleSave = async (type: string) => {
    try {
      const template = type === 'registration' ? registrationOTP : 
                      type === 'password' ? passwordReset : 
                      type === 'demo_request' ? demoRequest : welcome;
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/email-templates/${type}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ApiClient.getToken()}`
        },
        body: JSON.stringify(template),
      });

      if (!response.ok) throw new Error('Failed to save template');

      showSuccess('Email template saved successfully', "Success");
    } catch (error) {
      showError('Failed to save email template', "Error");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold">Email Templates</h1>
        <p className="text-gray-600 mt-2">Manage email templates for OTP and notifications</p>
      </div>

      <Tabs defaultValue="registration" className="w-full">
        <TabsList>
          <TabsTrigger value="registration">Registration OTP</TabsTrigger>
          <TabsTrigger value="password">Password Reset</TabsTrigger>
          <TabsTrigger value="welcome">Welcome Email</TabsTrigger>
          <TabsTrigger value="demo">Demo Request</TabsTrigger>
        </TabsList>

        <TabsContent value="registration">
          <Card>
            <CardHeader>
              <CardTitle>Registration OTP Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reg-subject">Subject</Label>
                <Input
                  id="reg-subject"
                  value={registrationOTP.subject}
                  onChange={(e) => setRegistrationOTP({ ...registrationOTP, subject: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-body">Email Body</Label>
                <Textarea
                  id="reg-body"
                  value={registrationOTP.body}
                  onChange={(e) => setRegistrationOTP({ ...registrationOTP, body: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-sm text-gray-500">
                  Available placeholders: {`{{OTP}}`}, {`{{firstName}}`}, {`{{lastName}}`}, {`{{email}}`}
                </p>
              </div>
              <Button onClick={() => handleSave('registration')}>Save Template</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>Password Reset Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pwd-subject">Subject</Label>
                <Input
                  id="pwd-subject"
                  value={passwordReset.subject}
                  onChange={(e) => setPasswordReset({ ...passwordReset, subject: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwd-body">Email Body</Label>
                <Textarea
                  id="pwd-body"
                  value={passwordReset.body}
                  onChange={(e) => setPasswordReset({ ...passwordReset, body: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-sm text-gray-500">Use {`{{OTP}}`} as placeholder for the OTP code</p>
              </div>
              <Button onClick={() => handleSave('password')}>Save Template</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="welcome">
          <Card>
            <CardHeader>
              <CardTitle>Welcome Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="welcome-subject">Subject</Label>
                <Input
                  id="welcome-subject"
                  value={welcome.subject}
                  onChange={(e) => setWelcome({ ...welcome, subject: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="welcome-body">Email Body</Label>
                <Textarea
                  id="welcome-body"
                  value={welcome.body}
                  onChange={(e) => setWelcome({ ...welcome, body: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-sm text-gray-500">
                  Available placeholders: {`{{firstName}}`}, {`{{lastName}}`}, {`{{email}}`}
                </p>
              </div>
              <Button onClick={() => handleSave('welcome')}>Save Template</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="demo">
          <Card>
            <CardHeader>
              <CardTitle>Demo Request Emails</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Customer Email</h3>
                <div className="space-y-2">
                  <Label htmlFor="demo-customer-subject">Subject</Label>
                  <Input
                    id="demo-customer-subject"
                    value={demoRequest.customerSubject}
                    onChange={(e) => setDemoRequest({ ...demoRequest, customerSubject: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="demo-customer-body">Email Body</Label>
                  <Textarea
                    id="demo-customer-body"
                    value={demoRequest.customerBody}
                    onChange={(e) => setDemoRequest({ ...demoRequest, customerBody: e.target.value })}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-sm text-gray-500">
                    Available placeholders: {`{{name}}`}, {`{{email}}`}
                  </p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold">Admin Notification Email</h3>
                <div className="space-y-2">
                  <Label htmlFor="demo-admin-subject">Subject</Label>
                  <Input
                    id="demo-admin-subject"
                    value={demoRequest.adminSubject}
                    onChange={(e) => setDemoRequest({ ...demoRequest, adminSubject: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="demo-admin-body">Email Body</Label>
                  <Textarea
                    id="demo-admin-body"
                    value={demoRequest.adminBody}
                    onChange={(e) => setDemoRequest({ ...demoRequest, adminBody: e.target.value })}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-sm text-gray-500">
                    Available placeholders: {`{{name}}`}, {`{{email}}`}, {`{{phone}}`}, {`{{dateTime}}`}
                  </p>
                </div>
              </div>

              <Button onClick={() => handleSave('demo_request')}>Save Template</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailTemplates;
